import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, ChevronRight, BookOpen, Users, Award, Shield, TrendingUp, Quote, ArrowRight, Star, CheckCircle, Facebook, Instagram, Linkedin, ExternalLink, Calendar, Eye, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { AUTHORS } from './AuthorPage';

const API = process.env.REACT_APP_BACKEND_URL;
const BOOK_URL = 'https://books.google.co.id/books?id=sES3EQAAQBAJ';
const COVER_URL = `${API}/api/static/book-cover.png`;

export default function BookLandingPage() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    document.title = 'Atomic Habits for Sharia Bankers — Pakde Heru';
    axios.get(`${API}/api/blog/articles?limit=4&sort=newest`).then(r => setArticles(r.data.articles || [])).catch(() => {});
    // SEO
    const desc = 'Buku Atomic Habits for Sharia Bankers menjelaskan bagaimana kebiasaan kecil para pemimpin menentukan masa depan institusi keuangan syariah.';
    const metas = [
      { name: 'description', content: desc },
      { name: 'keywords', content: 'atomic habits, sharia bankers, kepemimpinan syariah, pakde heru, buku keuangan syariah, Islamic banking leadership' },
      { property: 'og:title', content: 'Atomic Habits for Sharia Bankers — Pakde Heru' },
      { property: 'og:description', content: desc },
      { property: 'og:type', content: 'book' },
      { property: 'og:url', content: window.location.href },
    ];
    const created = [];
    metas.forEach(({ name, property, content }) => {
      const sel = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) { el = document.createElement('meta'); if (property) el.setAttribute('property', property); if (name) el.setAttribute('name', name); document.head.appendChild(el); created.push(el); }
      el.setAttribute('content', content);
    });
    return () => created.forEach(el => el.remove());
  }, []);

  const resolveImg = (url) => url?.startsWith('/api') ? `${API}${url}` : url;

  return (
    <div style={{ background: '#0A0A0A', color: '#EDEDED', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>

      {/* Responsive styles */}
      <style>{`
        .problem-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; margin-bottom: 2.5rem; }
        .preview-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; margin-bottom: 2.5rem; }
        @media (max-width: 768px) {
          .problem-grid { grid-template-columns: repeat(2, 1fr); }
          .preview-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .problem-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* NAV */}
      <nav data-testid="book-nav" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp size={22} color="#D4AF37" />
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', color: '#D4AF37', fontWeight: 700 }}>Pakde Heru</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <Link to="/" style={{ color: '#9CA3AF', textDecoration: 'none', fontSize: '0.85rem' }}>Blog</Link>
            <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-buy-btn" style={{
              background: '#D4AF37', color: '#0A0A0A', padding: '0.5rem 1.25rem', borderRadius: '2rem',
              textDecoration: 'none', fontSize: '0.8rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.375rem',
            }}>
              <ShoppingCart size={14} /> Beli Buku
            </a>
          </div>
        </div>
      </nav>

      {/* SECTION 1 — HERO */}
      <section data-testid="hero-section" style={{ paddingTop: '5rem', minHeight: '100vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 50%, rgba(212,175,55,0.06) 0%, transparent 50%), radial-gradient(ellipse at 70% 20%, rgba(212,175,55,0.04) 0%, transparent 40%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '3rem 1.5rem', display: 'flex', alignItems: 'center', gap: '4rem', flexWrap: 'wrap', position: 'relative' }}>
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>
            <p style={{ color: '#D4AF37', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1rem' }}>Buku Terbaru</p>
            <h1 data-testid="hero-title" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', lineHeight: 1.15, color: '#FFFFFF', marginBottom: '1.25rem', fontWeight: 700 }}>
              Mengapa Banyak Pemimpin Bank Syariah Gagal — dan Bagaimana <span style={{ color: '#D4AF37' }}>Kebiasaan Kecil</span> Menyelamatkan Institusi
            </h1>
            <p style={{ fontSize: '1.05rem', color: '#9CA3AF', lineHeight: 1.7, marginBottom: '2rem', maxWidth: '540px' }}>
              Buku <em>Atomic Habits for Sharia Bankers</em> menjelaskan bagaimana kebiasaan kecil para pemimpin menentukan masa depan institusi keuangan syariah.
            </p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '2rem' }}>
              <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-buy-btn" style={{
                background: '#D4AF37', color: '#0A0A0A', padding: '0.875rem 2rem', borderRadius: '2rem',
                textDecoration: 'none', fontWeight: 700, fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                boxShadow: '0 4px 24px rgba(212,175,55,0.25)', transition: 'transform 0.2s',
              }}>
                <ShoppingCart size={18} /> Beli Buku Sekarang
              </a>
              <span style={{ color: '#71717A', fontSize: '0.85rem' }}>144 halaman &middot; ISBN 9786340454208</span>
            </div>
            <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.8rem', color: '#71717A' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Star size={14} color="#D4AF37" /> 6 Penulis Ahli</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><BookOpen size={14} color="#D4AF37" /> Penerbit Resmi</span>
            </div>
          </div>
          <div style={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', inset: '-20px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)' }} />
              <img src={COVER_URL} alt="Atomic Habits for Sharia Bankers" data-testid="book-cover" style={{
                width: 'clamp(220px, 25vw, 340px)', borderRadius: '8px', position: 'relative',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(212,175,55,0.1)', transform: 'perspective(800px) rotateY(-5deg)',
              }} />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 — MASALAH BESAR */}
      <section data-testid="problem-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)' }}>
        <div style={{ maxWidth: '64rem', margin: '0 auto' }}>
          <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1rem', textAlign: 'center' }}>Krisis yang Tidak Terlihat</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', textAlign: 'center', lineHeight: 1.25, marginBottom: '2.5rem', color: '#FFFFFF' }}>
            Masalah Terbesar Bukan Strategi. <br /><span style={{ color: '#D4AF37' }}>Masalah Terbesar Adalah Kebiasaan Para Pemimpin.</span>
          </h2>
          <div className="problem-grid">
            {[
              { icon: <Shield size={22} />, title: 'Tekanan Regulasi', desc: 'Aturan OJK dan standar internasional yang semakin ketat menuntut kepatuhan sempurna — satu kesalahan bisa berujung sanksi yang mengancam reputasi institusi.' },
              { icon: <TrendingUp size={22} />, title: 'Tuntutan Kinerja', desc: 'Target profitabilitas yang agresif berbenturan dengan prinsip syariah yang tidak bisa dikompromikan. Pemimpin terjebak antara angka dan amanah.' },
              { icon: <Users size={22} />, title: 'Kompleksitas Governance', desc: 'Tata kelola berlapis — Dewan Pengawas Syariah, Komisaris, Direksi — membutuhkan integritas yang konsisten di setiap level keputusan.' },
              { icon: <Award size={22} />, title: 'Kompetisi Fintech', desc: 'Disrupsi digital mengancam relevansi institusi tradisional. Bank syariah yang lambat beradaptasi akan kehilangan generasi nasabah berikutnya.' },
              { icon: <BookOpen size={22} />, title: 'Krisis Kepemimpinan', desc: 'Banyak pemimpin diangkat karena kompetensi teknis, bukan kematangan emosional. Akibatnya, keputusan strategis sering didorong oleh tekanan, bukan visi.' },
              { icon: <Star size={22} />, title: 'Erosi Kepercayaan', desc: 'Setiap skandal di industri keuangan mengikis kepercayaan publik. Membangun kembali trust membutuhkan pemimpin yang integritasnya teruji dalam kebiasaan harian.' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.08)', borderRadius: '12px', padding: '1.5rem' }}>
                <div style={{ color: '#D4AF37', marginBottom: '0.75rem' }}>{item.icon}</div>
                <h4 style={{ fontSize: '0.9rem', color: '#EDEDED', marginBottom: '0.5rem', fontWeight: 600 }}>{item.title}</h4>
                <p style={{ fontSize: '0.8rem', color: '#9CA3AF', lineHeight: 1.6 }}>{item.desc}</p>
              </div>
            ))}
          </div>
          <blockquote style={{ borderLeft: '3px solid #D4AF37', paddingLeft: '1.25rem', margin: '0', fontStyle: 'italic', color: '#9CA3AF', fontSize: '1rem', lineHeight: 1.7 }}>
            "Setiap hari, pemimpin membuat ratusan keputusan kecil. Keputusan-keputusan itulah — bukan pidato tahunan — yang membentuk karakter institusi. Ketika kebiasaan pemimpin rusak, institusi hanya menunggu waktu untuk runtuh."
          </blockquote>
        </div>
      </section>

      {/* SECTION 3 — IDE UTAMA */}
      <section data-testid="idea-section" style={{ padding: '5rem 1.5rem', background: 'linear-gradient(180deg, rgba(212,175,55,0.03) 0%, transparent 100%)', borderTop: '1px solid rgba(212,175,55,0.08)' }}>
        <div style={{ maxWidth: '48rem', margin: '0 auto', textAlign: 'center' }}>
          <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1rem' }}>Gagasan Utama</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.4rem, 3vw, 2rem)', lineHeight: 1.3, marginBottom: '2rem', color: '#FFFFFF' }}>
            Institusi Tidak Runtuh Karena Tekanan Eksternal. <br /><span style={{ color: '#D4AF37' }}>Institusi Runtuh Karena Kebiasaan Para Pemimpinnya.</span>
          </h2>
          <p style={{ fontSize: '1rem', color: '#9CA3AF', lineHeight: 1.8, marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
            Buku ini menghubungkan framework <em>Atomic Habits</em> James Clear dengan prinsip kepemimpinan Islam — <strong style={{ color: '#D4AF37' }}>amanah</strong>, <strong style={{ color: '#D4AF37' }}>integritas</strong>, dan <strong style={{ color: '#D4AF37' }}>keberkahan institusi</strong>. Sebuah pendekatan yang belum pernah ada sebelumnya dalam literatur keuangan syariah.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap', marginTop: '2rem' }}>
            {['Amanah', 'Integritas', 'Keberanian Emosional', 'Keberkahan'].map((v, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#D4AF37', fontSize: '0.85rem', fontWeight: 600 }}>
                <CheckCircle size={16} /> {v}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — APA YANG DIPELAJARI */}
      <section data-testid="learn-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1rem', textAlign: 'center' }}>Isi Buku</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.4rem, 3vw, 2rem)', textAlign: 'center', marginBottom: '2.5rem', color: '#FFFFFF' }}>
            Apa yang Akan Anda Pelajari
          </h2>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {[
              'Bagaimana kebiasaan kecil membentuk integritas organisasi — dan mengapa perubahan 1% setiap hari menghasilkan transformasi institusional',
              'Mengapa keberanian emosional adalah kualitas paling langka dan paling dibutuhkan dalam kepemimpinan keuangan syariah',
              'Bagaimana pemimpin mempengaruhi budaya organisasi setiap hari melalui micro-decisions yang sering tidak disadari',
              'Hubungan mendalam antara habit, karakter, dan keberkahan — perspektif yang menggabungkan sains perilaku dengan wisdom Islam',
              'Framework praktis membangun kepemimpinan berintegritas yang tahan tekanan dalam industri keuangan syariah modern',
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.06)', borderRadius: '10px', padding: '1.25rem' }}>
                <span style={{ background: 'rgba(212,175,55,0.15)', color: '#D4AF37', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                <p style={{ fontSize: '0.9rem', color: '#CDCDCD', lineHeight: 1.6, margin: 0 }}>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — TENTANG PENULIS */}
      <AuthorsSection />

      {/* SECTION 6 — HUB KONTEN */}
      <section data-testid="content-hub-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.5rem', textAlign: 'center' }}>Insight Lain dari Pakde Heru</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.3rem, 2.5vw, 1.75rem)', textAlign: 'center', marginBottom: '2rem', color: '#FFFFFF' }}>
            Artikel Terbaru
          </h2>
          {articles.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1.25rem' }}>
              {articles.slice(0, 4).map(a => (
                <Link to={`/blog/${a.id}`} key={a.id} data-testid={`hub-article-${a.id}`} style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.06)', borderRadius: '10px',
                  padding: '1.25rem', textDecoration: 'none', color: 'inherit', transition: 'border-color 0.2s',
                }}>
                  {a.featured_image && <img src={resolveImg(a.featured_image)} alt="" style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '6px', marginBottom: '0.75rem' }} />}
                  <p style={{ fontSize: '0.65rem', color: '#D4AF37', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.375rem' }}>{a.category}</p>
                  <h4 style={{ fontFamily: "'Playfair Display', serif", fontSize: '0.95rem', color: '#EDEDED', lineHeight: 1.3, marginBottom: '0.5rem' }}>{a.title}</h4>
                  <span style={{ fontSize: '0.75rem', color: '#71717A', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Eye size={12} /> {a.views || 0} views
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#71717A' }}>Memuat artikel...</p>
          )}
          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Link to="/" style={{ color: '#D4AF37', fontSize: '0.85rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
              Lihat Semua Artikel <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 7 — PREVIEW BUKU */}
      <section data-testid="preview-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)', background: 'linear-gradient(180deg, rgba(212,175,55,0.03) 0%, transparent 100%)' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.5rem', textAlign: 'center' }}>Preview Buku</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.3rem, 2.5vw, 1.75rem)', textAlign: 'center', marginBottom: '2.5rem', color: '#FFFFFF' }}>Intip Isi Buku</h2>

          <div className="preview-grid">
            {/* Daftar Isi */}
            <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.08)', borderRadius: '12px', padding: '1.5rem' }}>
              <h3 style={{ fontSize: '0.9rem', color: '#D4AF37', marginBottom: '1rem', fontWeight: 700 }}>Daftar Isi</h3>
              {[
                'Mengapa Kebiasaan Menentukan Segalanya',
                'Identitas Pemimpin: Siapa Anda Sebenarnya?',
                'Atomic Habits dalam Konteks Syariah',
                'Keberanian Emosional: Fondasi Kepemimpinan',
                'Membangun Budaya Organisasi Berkah',
                'Dari Habit ke Karakter, dari Karakter ke Institusi',
                'Transformasi Digital dengan Integritas',
                'Blueprint Kepemimpinan Masa Depan',
              ].map((ch, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'baseline', padding: '0.4rem 0', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                  <span style={{ color: '#D4AF37', fontSize: '0.75rem', fontWeight: 700, minWidth: '1.5rem' }}>{i + 1}</span>
                  <span style={{ color: '#CDCDCD', fontSize: '0.85rem' }}>{ch}</span>
                </div>
              ))}
            </div>

            {/* Kutipan */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {[
                'Kepemimpinan sejati tidak diukur dari keputusan besar yang dramatis, tetapi dari konsistensi keputusan kecil yang berani.',
                'Integritas bukan tentang tidak pernah salah. Integritas adalah kebiasaan untuk selalu kembali ke jalan yang benar.',
                'Institusi yang berkah dibangun oleh pemimpin yang menjadikan amanah sebagai kebiasaan, bukan sekadar slogan.',
              ].map((q, i) => (
                <div key={i} style={{ background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.1)', borderRadius: '10px', padding: '1.25rem' }}>
                  <Quote size={16} color="#D4AF37" style={{ marginBottom: '0.5rem', opacity: 0.6 }} />
                  <p style={{ color: '#CDCDCD', fontSize: '0.9rem', lineHeight: 1.6, fontStyle: 'italic', margin: 0 }}>{q}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — TESTIMONI */}
      <section data-testid="testimonial-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
          <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '0.5rem', textAlign: 'center' }}>Testimoni</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.3rem, 2.5vw, 1.75rem)', textAlign: 'center', marginBottom: '2.5rem', color: '#FFFFFF' }}>
            Kata Mereka Tentang Buku Ini
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {[
              { name: 'Dr. Muhammad Syafii Antonio', title: 'Founder Tazkia Institute, Pakar Ekonomi Syariah', quote: 'Buku ini adalah terobosan luar biasa yang memadukan wisdom universal dengan nilai-nilai Islam. Setiap bankir syariah wajib membaca karya ini untuk memahami bagaimana kebiasaan kecil melahirkan kepemimpinan penuh barakah.' },
              { name: 'Prof. Mehmet Asutay', title: 'Professor of Islamic Political Economy, Durham University', quote: 'A remarkable integration of behavioral economics with Islamic leadership principles. This book provides practical frameworks for building sustainable organizations grounded in ethical courage.' },
              { name: 'Dr. Zamir Iqbal', title: 'Lead Financial Sector Specialist, World Bank', quote: 'This groundbreaking work bridges the gap between personal transformation and institutional excellence in Islamic banking. A compelling roadmap for leaders seeking to align profit with purpose.' },
            ].map((t, i) => (
              <div key={i} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.08)', borderRadius: '12px', padding: '1.5rem' }}>
                <Quote size={18} color="#D4AF37" style={{ opacity: 0.4, marginBottom: '0.75rem' }} />
                <p style={{ fontSize: '0.85rem', color: '#CDCDCD', lineHeight: 1.6, fontStyle: 'italic', marginBottom: '1rem' }}>"{t.quote}"</p>
                <p style={{ fontSize: '0.85rem', color: '#D4AF37', fontWeight: 600, marginBottom: '0.125rem' }}>{t.name}</p>
                <p style={{ fontSize: '0.75rem', color: '#71717A' }}>{t.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — FINAL CTA */}
      <section data-testid="final-cta-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)', background: 'linear-gradient(180deg, rgba(212,175,55,0.05) 0%, rgba(10,10,10,1) 100%)', textAlign: 'center' }}>
        <div style={{ maxWidth: '40rem', margin: '0 auto' }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.5rem, 3vw, 2.25rem)', lineHeight: 1.3, color: '#FFFFFF', marginBottom: '1rem' }}>
            Kepemimpinan Tidak Dibentuk Dalam Seminar. <br /><span style={{ color: '#D4AF37' }}>Kepemimpinan Dibentuk Dalam Kebiasaan Harian.</span>
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: '1rem', lineHeight: 1.7, marginBottom: '2rem' }}>
            Mulai perjalanan transformasi kepemimpinan Anda hari ini. Buku ini bukan sekadar bacaan — ini adalah blueprint untuk membangun institusi yang berkah.
          </p>
          <a href={BOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="final-buy-btn" style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            background: '#D4AF37', color: '#0A0A0A', padding: '1rem 2.5rem', borderRadius: '2rem',
            textDecoration: 'none', fontWeight: 700, fontSize: '1rem',
            boxShadow: '0 4px 24px rgba(212,175,55,0.3)',
          }}>
            <ShoppingCart size={18} /> Beli Buku Sekarang
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ padding: '2rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)', textAlign: 'center' }}>
        <div style={{ maxWidth: '56rem', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ color: '#71717A', fontSize: '0.8rem' }}>&copy; 2026 Pakde Heru — Strategic Thinker</p>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <Link to="/" style={{ color: '#71717A', fontSize: '0.8rem', textDecoration: 'none' }}>Blog</Link>
            <a href="https://facebook.com/pakdeheruu" target="_blank" rel="noopener noreferrer" style={{ color: '#71717A', fontSize: '0.8rem', textDecoration: 'none' }}>Facebook</a>
            <a href="https://instagram.com/pakdeheruu" target="_blank" rel="noopener noreferrer" style={{ color: '#71717A', fontSize: '0.8rem', textDecoration: 'none' }}>Instagram</a>
            <a href="https://linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer" style={{ color: '#71717A', fontSize: '0.8rem', textDecoration: 'none' }}>LinkedIn</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* Authors Section Component */
function AuthorsSection() {
  const [expanded, setExpanded] = useState(null);
  const heru = AUTHORS.find(a => a.slug === 'heru-muara-sidik');
  const coAuthors = AUTHORS.filter(a => a.slug !== 'heru-muara-sidik');

  return (
    <section data-testid="author-section" style={{ padding: '5rem 1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)', background: 'linear-gradient(180deg, rgba(212,175,55,0.03) 0%, transparent 100%)' }}>
      <div style={{ maxWidth: '56rem', margin: '0 auto' }}>
        <p style={{ color: '#D4AF37', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', marginBottom: '1rem', textAlign: 'center' }}>Tentang Penulis</p>

        {/* Lead Author — Pakde Heru */}
        {heru && (
          <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '3rem' }}>
            <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
              <img src={heru.photo} alt={heru.shortName} style={{ width: '160px', height: '200px', objectFit: 'cover', objectPosition: 'center top', borderRadius: '12px', border: '2px solid rgba(212,175,55,0.2)' }} />
              <p style={{ color: '#D4AF37', fontFamily: "'Playfair Display', serif", fontSize: '1.05rem', fontWeight: 700, marginTop: '0.75rem' }}>{heru.shortName}</p>
              <p style={{ color: '#71717A', fontSize: '0.7rem', maxWidth: '160px' }}>{heru.title}</p>
            </div>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <p style={{ color: '#CDCDCD', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '1rem' }}>
                Profesional dengan latar belakang <strong style={{ color: '#D4AF37' }}>akuntansi</strong>, <strong style={{ color: '#D4AF37' }}>audit</strong>, dan <strong style={{ color: '#D4AF37' }}>pengawasan investasi</strong> di industri keuangan syariah. Berpengalaman dalam pengawasan surat berharga, emas, serta penerapan teknologi informasi di sektor perbankan.
              </p>
              <p style={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1.7, marginBottom: '1rem' }}>
                Aktif melakukan penelitian di bidang <strong style={{ color: '#CDCDCD' }}>manajemen strategik</strong> dengan fokus pada <em>AI Capabilities</em>, <em>Innovation</em>, <em>Learning Organization</em>, dan <em>Sharia Governance</em> serta pengaruhnya terhadap kinerja perbankan syariah. Juga menghasilkan karya tulis tentang harmonisasi kerangka regulasi mata uang berbasis emas dalam sistem keuangan Islam dunia.
              </p>
              <p style={{ color: '#71717A', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>
                Melalui tulisan dan kontennya, Pakde Heru berbagi perspektif tentang bagaimana kebiasaan kecil dan keberanian emosional membentuk karakter pemimpin — khususnya di persimpangan antara tata kelola keuangan, kepatuhan syariah, dan transformasi digital.
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#D4AF37', fontSize: '0.8rem', textDecoration: 'none' }}><ExternalLink size={14} /> pakdeheru.com</Link>
                <a href="https://facebook.com/pakdeheruu" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#9CA3AF', fontSize: '0.8rem', textDecoration: 'none' }}><Facebook size={14} /> Facebook</a>
                <a href="https://instagram.com/pakdeheruu" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#9CA3AF', fontSize: '0.8rem', textDecoration: 'none' }}><Instagram size={14} /> Instagram</a>
                <a href="https://linkedin.com/in/herusidik" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#9CA3AF', fontSize: '0.8rem', textDecoration: 'none' }}><Linkedin size={14} /> LinkedIn</a>
              </div>
            </div>
          </div>
        )}

        {/* Co-Authors */}
        <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', color: '#FFFFFF', marginBottom: '1.25rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(212,175,55,0.08)' }}>
          Co-Authors
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {coAuthors.map((author) => (
            <div key={author.slug} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(212,175,55,0.06)', borderRadius: '12px', overflow: 'hidden' }}>
              <button
                onClick={() => setExpanded(expanded === author.slug ? null : author.slug)}
                data-testid={`author-toggle-${author.slug}`}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem',
                  background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}
              >
                <img src={author.photo} alt={author.shortName} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(212,175,55,0.15)' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: '#EDEDED', fontSize: '0.9rem', fontWeight: 600, margin: '0 0 2px' }}>{author.shortName}</p>
                  <p style={{ color: '#71717A', fontSize: '0.75rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{author.title}</p>
                </div>
                {expanded === author.slug ? <ChevronUp size={18} color="#D4AF37" /> : <ChevronDown size={18} color="#71717A" />}
              </button>

              {expanded === author.slug && (
                <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <p style={{ color: '#9CA3AF', fontSize: '0.85rem', lineHeight: 1.7, marginTop: '1rem', marginBottom: '0.75rem' }}>{author.bio}</p>
                  {author.expertise && (
                    <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                      {author.expertise.slice(0, 5).map((e, i) => (
                        <span key={i} style={{ padding: '2px 8px', borderRadius: '6px', fontSize: '0.65rem', background: 'rgba(212,175,55,0.08)', color: '#D4AF37' }}>{e}</span>
                      ))}
                    </div>
                  )}
                  <Link to={`/penulis/${author.slug}`} style={{ color: '#D4AF37', fontSize: '0.8rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
                    Profil Lengkap <ArrowRight size={12} />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
